/**
 * Geoapify Geocoding Autocomplete — city search across the platform's
 * active launch markets (currently DE + FR, see LAUNCH_COUNTRY_CODES).
 *
 * Why Geoapify rather than the offline `country-state-city` library or
 * Photon:
 *   • country-state-city shipped an 8 MB JSON of 150 k cities that
 *     blew the iOS Safari JS stack at boot.
 *   • Photon (Komoot) is free + key-less but its German index is less
 *     forgiving on alt spellings (Giessen vs Gießen, etc.).
 *   • Geoapify's autocomplete handles ß/ss and translit out of the box,
 *     gives stable place_ids, returns a `state` (Bundesland / région),
 *     and the free tier (3 000 req/day) is plenty for a launch-market
 *     app.
 *
 * Endpoint:
 *   GET https://api.geoapify.com/v1/geocode/autocomplete
 *   Docs: https://www.geoapify.com/geocoding-autocomplete-api
 *
 * Security:
 *   The API key is exposed to the browser (VITE_* vars get inlined into
 *   the client bundle — that's how Vite works). Lock it down in the
 *   Geoapify dashboard via "Allowed referrers" so only your domains can
 *   spend your quota. Without that allowlist anyone reading the bundle
 *   can scrape the key. See .env.example.
 */

const GEOAPIFY_BASE = 'https://api.geoapify.com/v1/geocode/autocomplete';

/**
 * The exact subset of fields we store after the user picks a city.
 * Anything Geoapify also returns (admin1 codes, postcodes, formatted
 * line, …) is deliberately dropped — keeping the payload small means
 * the picker can save it inline in URL params, localStorage, or POST
 * bodies without thinking twice.
 */
export interface GeoapifyCity {
  city: string;        // e.g. "Gießen" / "Lyon"
  region: string;      // Bundesland / région, e.g. "Hessen". Empty when missing.
  country: string;     // Localised, e.g. "Deutschland" / "France"
  countryCode: string; // ISO alpha-2 uppercase, e.g. "DE" / "FR"
  latitude: number;
  longitude: number;
  placeId: string;     // Geoapify's stable id — useful for dedup
}

export interface SearchCitiesOpts {
  /** Caller-provided controller so the consumer can cancel in-flight
   *  requests when a newer keystroke supersedes this one. */
  signal?: AbortSignal;
  /** Display language for `country` / `region` strings. Omitted from
   *  the URL when undefined so Geoapify can pick the best label for
   *  the matched feature. */
  lang?: string;
  /** Max suggestions returned. Clamped to 1-10 per Geoapify's contract. */
  limit?: number;
  /** Restrict results to these ISO-3166-1 alpha-2 country codes.
   *  Passed as a comma-separated `countrycode:de,fr` filter. When
   *  empty / missing the search spans every country Geoapify knows. */
  countryCodes?: readonly string[];
}

/**
 * Single source of truth for "what cities match this typing?".
 *
 * Contract:
 *   • Returns at most {@code limit} (default 8, capped at 10) cities,
 *     restricted to {@code countryCodes} when provided.
 *   • Returns [] for queries shorter than 2 chars — Geoapify itself
 *     also rejects them, this just saves a round-trip.
 *   • Returns [] (does NOT throw) on network / abort / non-200, so
 *     callers can render an empty dropdown without try/catch noise.
 *   • Deduplicates by `place_id` — Geoapify occasionally repeats the
 *     same place with slightly different ranks across requests.
 */
export async function searchCitiesGeoapify(
  query: string,
  opts: SearchCitiesOpts = {},
): Promise<GeoapifyCity[]> {
  const apiKey = (import.meta.env.VITE_GEOAPIFY_API_KEY as string | undefined)?.trim();
  if (!apiKey) return [];

  const q = query.trim();
  if (q.length < 2) return [];

  const limit = Math.min(Math.max(opts.limit ?? 8, 1), 10);

  const params = new URLSearchParams({
    text: q,
    // `type=city` restricts to populated places (cities, towns,
    // villages) and excludes streets, POIs, regions, etc.
    type: 'city',
    limit: String(limit),
    apiKey,
  });
  // Country filter — Geoapify expects lowercase ISO-2, comma-separated.
  // Omit the filter entirely when no country is requested so the search
  // doesn't silently drop to "Germany only".
  const codes = (opts.countryCodes ?? [])
    .filter((c) => typeof c === 'string' && c.length === 2)
    .map((c) => c.toLowerCase());
  if (codes.length > 0) {
    params.set('filter', `countrycode:${codes.join(',')}`);
  }
  if (opts.lang) params.set('lang', opts.lang);

  let res: Response;
  try {
    res = await fetch(`${GEOAPIFY_BASE}?${params.toString()}`, {
      signal: opts.signal,
      credentials: 'omit',
    });
  } catch {
    // AbortError (caller cancelled) and genuine network errors both
    // land here. The caller already knows we cancelled if it was the
    // one that aborted; for network errors, an empty dropdown is the
    // friendly degradation.
    return [];
  }

  if (!res.ok) return [];

  let data: {
    features?: Array<{
      properties?: {
        city?: string;
        name?: string;
        state?: string;
        country?: string;
        country_code?: string;
        lat?: number;
        lon?: number;
        place_id?: string;
      };
      geometry?: { coordinates?: [number, number] };
    }>;
  };
  try {
    data = await res.json();
  } catch {
    return [];
  }

  const features = Array.isArray(data?.features) ? data.features : [];
  const out: GeoapifyCity[] = [];
  const seen = new Set<string>();

  for (const f of features) {
    const p = f.properties ?? {};
    // Some Geoapify rows put the city name in `name` (smaller places)
    // and the bigger ones in `city`. Try both before bailing.
    const cityName = p.city ?? p.name;
    if (!cityName) continue;

    // place_id is the cleanest dedup key. Skip rows missing it rather
    // than guess — they're rare and usually low-quality matches.
    const placeId = p.place_id ?? '';
    if (!placeId || seen.has(placeId)) continue;
    seen.add(placeId);

    // Geoapify returns coordinates both inline (lat/lon) and via
    // geometry.coordinates [lon, lat]. Prefer the inline ones.
    const lat = typeof p.lat === 'number' ? p.lat : f.geometry?.coordinates?.[1];
    const lon = typeof p.lon === 'number' ? p.lon : f.geometry?.coordinates?.[0];
    if (typeof lat !== 'number' || typeof lon !== 'number') continue;

    out.push({
      city: cityName,
      region: p.state ?? '',
      country: p.country ?? '',
      countryCode: (p.country_code ?? '').toUpperCase(),
      latitude: lat,
      longitude: lon,
      placeId,
    });
  }
  return out;
}
